import { useMemo, useState } from 'react';
import type { ProjectionYear } from '../engine/projection';
import type { Assumptions, ContributionTaxTreatment } from '../types';
import { fmtMoney } from '../utils/format';
import { GlossaryTerm } from './GlossaryTerm';
import {
  buildYearRhythm,
  computeYearAnnualTotals,
  type RhythmBand,
  type RhythmInflow,
  type RhythmOutflow,
  type YearRhythm,
} from './year-rhythm';

// ────────────────────────────────────────────────────────────────────────────
// <YearRhythmStrip> — annual cash-flow summary for a single projection year.
//
// Three structured-text cards:
//   1. One-time this year — discrete inflows + lump/with-bonus contributions
//      grouped by month. Months with no entries are skipped entirely (no
//      empty calendar lanes).
//   2. Every month — flat list of recurring monthly streams (contribution
//      rules with monthly / with_paycheck schedules + mandatory monthly
//      outflows + active-loan P&I).
//   3. Annual totals — Inflows, After-tax, Mandatory out, Discretionary out,
//      Net cash. Pulled from the engine's `YearBudget` so totals reconcile
//      with the projection.
//
// Year picker arrows (◀ / ▶) step across the projection horizon. Rows are
// click-through to the relevant editor: bonus / RSU rows go to Assumptions,
// contribution rows go to Investments, mandatory rows go to Assumptions.
//
// IRS jargon (`402(g)`, `415(c)`, `mega backdoor`, `pretax`) is wrapped in
// `<GlossaryTerm>` per the project's educational principle.
// ────────────────────────────────────────────────────────────────────────────

const MONTH_LONG = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

interface Props {
  /** Full projection (used for picker bounds and per-year inputs). */
  years: ProjectionYear[];
  assumptions: Assumptions;
  /** Optional handler to deep-link from a row into an editor. */
  onGoTo?: (tab: string) => void;
  /** Default year offset. Falls back to 0 (current year). */
  initialYearOffset?: number;
  /** Compact variant (slimmer padding, used when stacked alongside other strips). */
  compact?: boolean;
}

export function YearRhythmStrip({
  years,
  assumptions,
  onGoTo,
  initialYearOffset = 0,
  compact = false,
}: Props) {
  const maxOffset = Math.max(0, years.length - 1);
  const [yearOffset, setYearOffset] = useState(() =>
    Math.max(0, Math.min(initialYearOffset, maxOffset)),
  );

  const projYear = years[yearOffset] ?? years[0];

  const rhythm: YearRhythm = useMemo(() => {
    if (!projYear) {
      return {
        yearOffset,
        calendarYear: assumptions.startingCalendarYear + yearOffset,
        inflows: [],
        outflows: [],
        bands: [],
      };
    }
    return buildYearRhythm({
      yearOffset,
      calendarYear: projYear.calendarYear,
      compensation: assumptions.compensation,
      rules: assumptions.contributionRules ?? [],
      monthlyHouseholdSpend: assumptions.monthlyHouseholdSpend,
      monthlyMortgagePI: assumptions.mortgageMonthlyPI,
      monthlyEscrow: assumptions.mortgageMonthlyEscrow,
      annualHealthcare: assumptions.healthcareAnnual,
      activeLoans: projYear.loans.map(l => ({
        id: l.id,
        label: l.label,
        monthlyPI: l.monthlyPI,
      })),
      netCashSurplus: projYear.budget?.netCashSurplus,
    });
  }, [projYear, yearOffset, assumptions]);

  const totals = useMemo(
    () =>
      computeYearAnnualTotals({
        rhythm,
        baseAnnual: assumptions.compensation?.baseAnnual ?? 0,
        budget: projYear?.budget,
      }),
    [rhythm, assumptions.compensation?.baseAnnual, projYear?.budget],
  );

  // Group one-time entries by month.
  const oneTimeByMonth = useMemo(
    () => groupOneTimeByMonth(rhythm, assumptions.contributionRules ?? []),
    [rhythm, assumptions.contributionRules],
  );

  // Recurring monthly streams (all bands).
  const recurringRows = useMemo(
    () => buildRecurringRows(rhythm),
    [rhythm],
  );

  const handleNav = (target: 'assumptions' | 'investments') => {
    if (onGoTo) onGoTo(target);
  };

  const padCls = compact ? 'p-3' : 'p-3 sm:p-4';
  const age = projYear?.age ?? assumptions.currentAge + yearOffset;

  return (
    <section className="space-y-3" aria-label="Year cash flow summary">
      {/* Header: year picker + net cash callout */}
      <header className={`card card-dark ${padCls} flex flex-wrap items-center justify-between gap-2`}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setYearOffset(o => Math.max(0, o - 1))}
            disabled={yearOffset === 0}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-ink-200 bg-white text-ink-600 transition hover:border-accent-400 hover:text-accent-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-200"
            aria-label="Previous year"
          >
            ◀
          </button>
          <div className="flex flex-col">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
              How your year flows
            </div>
            <div className="font-mono text-sm font-semibold tabular-nums text-ink-900 dark:text-ink-50">
              {rhythm.calendarYear}
              <span className="ml-2 text-[11px] font-normal text-ink-500 dark:text-ink-300">
                age {age}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setYearOffset(o => Math.min(maxOffset, o + 1))}
            disabled={yearOffset >= maxOffset}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-ink-200 bg-white text-ink-600 transition hover:border-accent-400 hover:text-accent-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-200"
            aria-label="Next year"
          >
            ▶
          </button>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
            Net cash this year
          </div>
          <div
            className={`font-mono text-sm font-semibold tabular-nums ${toneForNet(totals.netCash)}`}
            title="cash in − mandatory out − discretionary out − contributions actually made (from the engine's per-year budget reconciler)"
          >
            {fmtMoney(totals.netCash, { sign: true })}
          </div>
        </div>
      </header>

      {/* 1. One-time this year — grouped by month */}
      <div className={`card card-dark ${padCls}`}>
        <SectionTitle title="One-time this year" />
        {oneTimeByMonth.length === 0 ? (
          <p className="text-[12px] text-ink-500 dark:text-ink-300">
            Nothing one-time scheduled for {rhythm.calendarYear}.
          </p>
        ) : (
          <div className="mt-1.5 divide-y divide-ink-100 dark:divide-ink-700/60">
            {oneTimeByMonth.map(group => (
              <div key={group.month} className="py-1.5 first:pt-0 last:pb-0">
                <div className="grid grid-cols-[3rem_1fr] gap-2">
                  <div className="pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
                    {MONTH_LONG[group.month - 1]}
                  </div>
                  <ul className="space-y-1">
                    {group.entries.map(entry => (
                      <OneTimeRow
                        key={entry.id}
                        entry={entry}
                        onNav={handleNav}
                      />
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2. Every month */}
      <div className={`card card-dark ${padCls}`}>
        <SectionTitle title="Every month" />
        {recurringRows.length === 0 ? (
          <p className="text-[12px] text-ink-500 dark:text-ink-300">
            No recurring monthly streams configured.
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1 divide-y divide-ink-100 dark:divide-ink-700/60">
            {recurringRows.map(row => (
              <RecurringRow key={row.id} row={row} onNav={handleNav} />
            ))}
          </ul>
        )}
      </div>

      {/* 3. Annual totals */}
      <div className={`card card-dark ${padCls}`}>
        <SectionTitle title="Annual totals" />
        <ul className="mt-1.5 space-y-1 text-[12px]">
          <TotalsRow
            label="Inflows"
            sublabel="base + bonus + match + RSU"
            amount={totals.inflows}
            kind="in"
          />
          <TotalsRow
            label="After tax"
            sublabel={
              <>
                cash actually in hand{' '}
                <GlossaryTerm term="effective_rate">est.</GlossaryTerm>
              </>
            }
            amount={totals.afterTax}
            kind="in"
          />
          <TotalsRow
            label="Mandatory out"
            sublabel="spend + housing + healthcare + loan P&I"
            amount={-Math.abs(totals.mandatoryOut)}
            kind="out"
          />
          <TotalsRow
            label="Discretionary out"
            sublabel="contributions + cash-flow purchases"
            amount={-Math.abs(totals.discretionaryOut)}
            kind="out"
          />
          <li className="border-t border-ink-200 pt-1 dark:border-ink-700">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[12px] font-semibold text-ink-900 dark:text-ink-50">
                Net cash
              </span>
              <span
                className={`font-mono text-sm font-bold tabular-nums ${toneForNet(totals.netCash)}`}
              >
                {fmtMoney(totals.netCash, { sign: true })}
              </span>
            </div>
          </li>
        </ul>
        <p className="mt-2 text-[10px] leading-snug text-ink-500 dark:text-ink-300">
          Real-dollar values from your{' '}
          <GlossaryTerm term="contribution_rule">contribution rules</GlossaryTerm>
          , compensation, and the engine's per-year budget reconciler. The
          engine grows each deposit{' '}
          <GlossaryTerm term="time_weighted_growth">time-weighted</GlossaryTerm>:
          a January lump-sum gets ~12 months of growth, monthly streams get ~6.5.
        </p>
      </div>
    </section>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
      {title}
    </div>
  );
}

interface OneTimeEntry {
  id: string;
  /** Signed amount: positive for inflow, negative for outflow/saved. */
  amount: number;
  name: string;
  /** Optional cap / treatment chip (rendered inside <GlossaryTerm>). */
  capChip?: { term: string; label: string };
  /** Where to navigate on click. */
  navTarget: 'assumptions' | 'investments' | null;
  /** Used to color the row (in / out / save). */
  kind: 'in' | 'out' | 'save';
}

function OneTimeRow({
  entry,
  onNav,
}: {
  entry: OneTimeEntry;
  onNav: (target: 'assumptions' | 'investments') => void;
}) {
  const clickable = entry.navTarget != null;
  const handle = clickable && entry.navTarget
    ? () => onNav(entry.navTarget!)
    : undefined;

  const RowTag = clickable ? 'button' : 'div';
  const rowProps = clickable
    ? {
        type: 'button' as const,
        onClick: handle,
        className:
          'group/row flex w-full items-baseline justify-between gap-3 rounded text-left transition hover:bg-ink-50/60 dark:hover:bg-ink-700/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
      }
    : {
        className:
          'flex w-full items-baseline justify-between gap-3',
      };

  return (
    <li>
      <RowTag {...rowProps}>
        <span className="flex flex-wrap items-baseline gap-1.5 text-[12px] text-ink-800 dark:text-ink-100">
          <span className="font-medium">{entry.name}</span>
          {entry.capChip && (
            <span className="rounded-full border border-ink-200 bg-ink-50 px-1.5 py-0 text-[10px] font-medium text-ink-600 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-300">
              <GlossaryTerm term={entry.capChip.term}>{entry.capChip.label}</GlossaryTerm>
            </span>
          )}
        </span>
        <span
          className={`shrink-0 font-mono text-[12px] font-semibold tabular-nums ${toneForFlow(entry.kind)}`}
        >
          {entry.amount >= 0 ? '+' : '−'}
          {fmtMoney(Math.abs(entry.amount))}
        </span>
      </RowTag>
    </li>
  );
}

interface RecurringRowItem {
  id: string;
  /** Per-month dollar amount in today's $. Always shown as a negative-styled
   *  outflow (savings + mandatory both leave the user's wallet each month). */
  monthlyAmount: number;
  name: string;
  /** Subtle one-line subtitle (e.g., "saved monthly", "P&I excludes escrow"). */
  subtitle?: string;
  navTarget: 'assumptions' | 'investments' | null;
  /** Visual tone — saving (accent) vs mandatory (muted). */
  kind: 'save' | 'out';
}

function RecurringRow({
  row,
  onNav,
}: {
  row: RecurringRowItem;
  onNav: (target: 'assumptions' | 'investments') => void;
}) {
  const clickable = row.navTarget != null;
  const handle = clickable && row.navTarget
    ? () => onNav(row.navTarget!)
    : undefined;

  const RowTag = clickable ? 'button' : 'div';
  const rowProps = clickable
    ? {
        type: 'button' as const,
        onClick: handle,
        className:
          'group/row flex w-full items-baseline justify-between gap-3 rounded py-1 text-left transition hover:bg-ink-50/60 dark:hover:bg-ink-700/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
      }
    : {
        className:
          'flex w-full items-baseline justify-between gap-3 py-1',
      };

  return (
    <li>
      <RowTag {...rowProps}>
        <span className="flex flex-col text-[12px] text-ink-800 dark:text-ink-100">
          <span className="font-medium">{row.name}</span>
          {row.subtitle && (
            <span className="text-[10px] text-ink-500 dark:text-ink-300">
              {row.subtitle}
            </span>
          )}
        </span>
        <span
          className={`shrink-0 text-right font-mono text-[12px] tabular-nums ${toneForFlow(row.kind)}`}
        >
          <span className="font-semibold">
            −{fmtMoney(Math.abs(row.monthlyAmount))}
          </span>
          <span className="ml-1 text-[10px] text-ink-500 dark:text-ink-300">
            /mo
          </span>
        </span>
      </RowTag>
    </li>
  );
}

function TotalsRow({
  label,
  sublabel,
  amount,
  kind,
}: {
  label: string;
  sublabel?: React.ReactNode;
  amount: number;
  kind: 'in' | 'out';
}) {
  const sign = amount >= 0 ? '+' : '−';
  const tone =
    kind === 'in'
      ? 'text-pos-700 dark:text-pos-500'
      : 'text-ink-700 dark:text-ink-200';
  return (
    <li className="flex items-baseline justify-between gap-3">
      <span className="flex flex-col">
        <span className="text-[12px] text-ink-800 dark:text-ink-100">
          {label}
        </span>
        {sublabel && (
          <span className="text-[10px] text-ink-500 dark:text-ink-300">
            {sublabel}
          </span>
        )}
      </span>
      <span className={`shrink-0 font-mono text-[12px] font-semibold tabular-nums ${tone}`}>
        {sign}
        {fmtMoney(Math.abs(amount))}
      </span>
    </li>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

interface OneTimeMonthGroup {
  month: number;
  entries: OneTimeEntry[];
}

/** Group one-time entries by calendar month, sorted month-asc, with each
 *  month's entries sorted largest-magnitude first. */
function groupOneTimeByMonth(
  rhythm: YearRhythm,
  rules: { id: string; taxTreatment?: ContributionTaxTreatment }[],
): OneTimeMonthGroup[] {
  const ruleTreatment = new Map<string, ContributionTaxTreatment | undefined>();
  for (const r of rules) ruleTreatment.set(r.id, r.taxTreatment);

  const byMonth = new Map<number, OneTimeEntry[]>();

  for (const inflow of rhythm.inflows) {
    const entry = oneTimeFromInflow(inflow);
    if (!entry) continue;
    const list = byMonth.get(inflow.month) ?? [];
    list.push(entry);
    byMonth.set(inflow.month, list);
  }
  for (const outflow of rhythm.outflows) {
    const entry = oneTimeFromOutflow(outflow, ruleTreatment.get(outflow.ruleId ?? ''));
    if (!entry) continue;
    const list = byMonth.get(outflow.month) ?? [];
    list.push(entry);
    byMonth.set(outflow.month, list);
  }

  const groups: OneTimeMonthGroup[] = [];
  for (const [month, entries] of byMonth) {
    entries.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    groups.push({ month, entries });
  }
  groups.sort((a, b) => a.month - b.month);
  return groups;
}

function oneTimeFromInflow(inflow: RhythmInflow): OneTimeEntry | null {
  if (inflow.kind === 'bonus') {
    return {
      id: inflow.id,
      amount: inflow.amount,
      name: 'Bonus',
      navTarget: 'assumptions',
      kind: 'in',
    };
  }
  if (inflow.kind === 'rsu') {
    return {
      id: inflow.id,
      amount: inflow.amount,
      name: 'RSU vest',
      navTarget: 'assumptions',
      kind: 'in',
    };
  }
  // employer_match (lump or with-bonus shape — monthly match is rendered as
  // a band, so it lands in the "Every month" section, not here).
  return {
    id: inflow.id,
    amount: inflow.amount,
    name: 'Employer match (to 401(k))',
    navTarget: 'investments',
    kind: 'in',
  };
}

function oneTimeFromOutflow(
  outflow: RhythmOutflow,
  treatment: ContributionTaxTreatment | undefined,
): OneTimeEntry | null {
  return {
    id: outflow.id,
    amount: -Math.abs(outflow.amount),
    name: outflow.label,
    capChip: capChipFor(treatment),
    navTarget: 'investments',
    kind: 'save',
  };
}

/** Map a contribution rule's tax treatment to a small jargon chip the user
 *  can click to look up the relevant IRS limit. */
function capChipFor(
  treatment: ContributionTaxTreatment | undefined,
): { term: string; label: string } | undefined {
  switch (treatment) {
    case 'pretax':
      return { term: 'irs_402g', label: '402(g) pretax' };
    case 'roth_401k':
      return { term: 'irs_402g', label: '402(g) Roth 401(k)' };
    case 'after_tax_401k':
      return { term: 'irs_415c', label: '415(c) after-tax' };
    case 'mega_backdoor_to_roth':
      return { term: 'mega_backdoor', label: 'mega backdoor' };
    case 'roth_ira':
      return { term: 'roth_ira', label: 'Roth IRA' };
    case 'after_tax_brokerage':
      return undefined;
    default:
      return undefined;
  }
}

/** Build the recurring-monthly rows from rhythm bands. */
function buildRecurringRows(rhythm: YearRhythm): RecurringRowItem[] {
  return rhythm.bands.map(band => recurringRowFromBand(band));
}

function recurringRowFromBand(band: RhythmBand): RecurringRowItem {
  switch (band.kind) {
    case 'contribution_monthly': {
      // Employer match streamed monthly is a rare shape — show it as an
      // inflow note rather than a saving outflow.
      const isEmployerMatch = band.label === 'Employer match';
      return {
        id: band.id,
        monthlyAmount: band.monthlyAmount,
        name: band.label,
        subtitle: isEmployerMatch
          ? 'employer-paid into 401(k)'
          : 'saved monthly',
        navTarget: 'investments',
        kind: 'save',
      };
    }
    case 'spend':
      return {
        id: band.id,
        monthlyAmount: band.monthlyAmount,
        name: 'Household spend',
        navTarget: 'assumptions',
        kind: 'out',
      };
    case 'mortgage_pi':
      return {
        id: band.id,
        monthlyAmount: band.monthlyAmount,
        name: 'Mortgage P&I',
        subtitle: 'principal + interest',
        navTarget: 'assumptions',
        kind: 'out',
      };
    case 'escrow':
      return {
        id: band.id,
        monthlyAmount: band.monthlyAmount,
        name: 'Escrow',
        subtitle: 'property taxes & insurance',
        navTarget: 'assumptions',
        kind: 'out',
      };
    case 'healthcare':
      return {
        id: band.id,
        monthlyAmount: band.monthlyAmount,
        name: 'Healthcare',
        navTarget: 'assumptions',
        kind: 'out',
      };
    case 'loan_pi':
      return {
        id: band.id,
        monthlyAmount: band.monthlyAmount,
        name: band.label,
        subtitle: 'loan P&I',
        navTarget: null,
        kind: 'out',
      };
  }
}

function toneForFlow(kind: 'in' | 'out' | 'save'): string {
  switch (kind) {
    case 'in':
      return 'text-pos-700 dark:text-pos-500';
    case 'save':
      return 'text-accent-700 dark:text-accent-500';
    case 'out':
      return 'text-ink-600 dark:text-ink-300';
  }
}

function toneForNet(net: number): string {
  if (net >= 0) return 'text-pos-700 dark:text-pos-500';
  return 'text-amber-700 dark:text-amber-500';
}
